/**
 * The form-performance section of the Dynamics board (L1): the hourly P95
 * trend, the shape of the load-duration distribution, when the load actually
 * arrives, and the five slowest forms.
 *
 * ## Whose measurement this is, and why the section says so
 *
 * Every number here comes from **F&O's own telemetry** (`AppPageViews`, one row
 * per form load with its `DurationMs`). No agent, no extension, no opt-in: every
 * user in every legal entity is in it, which is what makes this the complete
 * population — measured 2026-08-22 at 269,725 form loads across 31 companies in
 * 24 hours.
 *
 * That matters because NOVA has a *second* view of the same people:
 * `/experience`, where a managed Edge extension measures F&O in the browser
 * alongside four other apps. On the same day it covered **one** of those 31
 * companies and 17 of those 269,725 loads. The two boards are not two opinions
 * of one estate — this one is the population, that one is a sample of it whose
 * size is its own first subject. The section's first line says so on screen,
 * because a reader who takes the two for equals will read a thin browser chart
 * as evidence that the estate is fine.
 *
 * Form form: a trend is a line, a distribution is a histogram, hour-of-day
 * volume is a bar, and a ranking is a "worst 5" grid with the rest one click
 * deeper (§2, §1). No dual axes: the P95 line and the load counts live on
 * separate cards rather than sharing a plot with two y-scales. Every card states
 * the loads behind it, so no percentile here is read without its sample.
 *
 * This board ranks forms, never people. Any form used by fewer than five
 * distinct users is folded by the backend into a single unlabelled `Other` row,
 * and the fold is reported rather than hidden — it renders as the honesty
 * caption under the grid, not as a silently shorter list.
 *
 * The `Other` row carries `loads` but no `p95_ms`, `p99_ms` or `distinct_users`,
 * because a percentile of percentiles is not a percentile. Those cells render as
 * an em dash. Printing `0 ms` there would claim the slowest unlabelled forms in
 * the estate are the fastest thing on the page.
 */

import { Chart } from "@norican/dataviz";
import { CircleCheck, Icon, TriangleAlert } from "@norican/icons";
import { Badge, DataGrid, EmptyState, Typography } from "@norican/react";

import { ChartCard } from "@/components/board";
import { EXPERIENCE_PATH } from "@/features/experience/params";
import type { FoDurationBand, FoFormPerformance, FoHourlyLoad, FoUserExperience } from "@/lib/api";
import { DrillLink } from "@/lib/drill";

import { PanelCard } from "./panel-card";
import { DYNAMICS_FORMS_PATH, EXPERIENCE_ANCHOR } from "./params";
import {
  formatBinDateTime,
  formatCount,
  formatHourUtc,
  formatMs,
  formatMsCompact,
  formatPercent,
  formatWindow,
  suppressionCaption,
  tooltipValue,
} from "./status";

/** How many labelled forms the L1 grid ranks — the rest live at L2. */
const WORST_LIMIT = 5;

/** Hours in a day, for the load-by-hour coverage caption. */
const HOURS_IN_DAY = 24;

/**
 * What the ranking is a slice of.
 *
 * `worst_forms` is the backend's fifteen slowest, of which this grid shows five;
 * `forms_total` is how many labelled form cohorts the window actually held,
 * counted before that truncation. Without this line the board's largest number
 * about forms is "5", which reads as an estate with five forms in it. A `null`
 * total is the counting query having failed, and says so rather than borrowing
 * the ranked count as if it were the population.
 */
function rankingScopeCaption(ranked: number, total: number | null): string {
  if (total == null) {
    return `${formatCount(ranked)} forms are ranked. How many the window held in all could not be counted, so this is the worst of a population of unknown size.`;
  }
  const rest = Math.max(total - ranked, 0);
  return `${formatCount(ranked)} of the ${formatCount(total)} labelled forms in the window are ranked, slowest P95 first. The other ${formatCount(rest)} were measured too and ranked below them.`;
}

interface ExperienceSectionProps {
  experience: FoUserExperience;
}

export function ExperienceSection({ experience }: ExperienceSectionProps) {
  const windowLabel = formatWindow(experience.window);
  const staleAfterMs = experience.cache_ttl_sec * 2000;

  // A bin whose p95 is null stays in the data with a null value, so the line
  // breaks there. A quiet hour is a gap, never a drop to zero latency.
  const trendRows = experience.p95_trend.map((point) => ({
    bucket: point.bucket,
    label: formatBinDateTime(point.bucket),
    p95_ms: point.p95_ms,
    avg_ms: point.avg_ms,
    loads: point.loads,
  }));

  // Bands are emitted complete or not at all: when the query returned rows,
  // every band is present and an empty one is a genuine zero; when it returned
  // nothing, there are no bands, which is no data. Hence the two branches.
  const bands = experience.duration_bands;
  const bandTotal = bands.reduce((sum, band) => sum + band.loads, 0);

  // `load_by_hour` carries only the hours the query returned, so it is NOT
  // padded to 0–23 here. An absent hour is an hour nothing was recorded for,
  // and filling it with a zero would invent 24-hour coverage the query never
  // claimed. The caption states how many hours are actually represented.
  const hourRows = [...experience.load_by_hour]
    .sort((a, b) => a.hour_of_day - b.hour_of_day)
    .map((row) => ({ ...row, label: formatHourUtc(row.hour_of_day) }));

  const labelledForms = experience.worst_forms.filter((form) => !form.is_other);
  const otherForm = experience.worst_forms.find((form) => form.is_other);
  const worstForms = [...labelledForms.slice(0, WORST_LIMIT), ...(otherForm ? [otherForm] : [])];

  // The window's load count, for the "what is this measured over" captions. The
  // histogram is the more complete count when it answered; the trend is the
  // fallback, and both being empty means nothing was measured rather than zero.
  const trendLoads = experience.p95_trend.reduce((sum, point) => sum + point.loads, 0);
  const loadsTotal = bandTotal > 0 ? bandTotal : trendLoads;

  return (
    <section id={EXPERIENCE_ANCHOR} className="flex scroll-mt-6 flex-col gap-4">
      {/* Whose measurement this is. It is the section's first line because the
          same estate has a second, much thinner view of itself on /experience,
          and the two must not be read as equals. */}
      <Typography variant="body-sm" tone="muted">
        F&amp;O&apos;s own form-load telemetry, covering every user in every legal entity with
        nothing to install:{" "}
        {loadsTotal > 0
          ? `${formatCount(loadsTotal)} loads in ${windowLabel}`
          : `no load measured in ${windowLabel}`}
        . The managed browser extension measures the same forms from the endpoint side and reaches a
        fraction of these people, so read that board as a sample of this population and not as a
        second opinion on it —{" "}
        <DrillLink to={EXPERIENCE_PATH} className="text-accent font-medium hover:underline">
          how large a fraction
        </DrillLink>{" "}
        is its own first panel.
      </Typography>

      <ChartCard<(typeof trendRows)[number]>
        title="Form load P95, hourly"
        caption={`${windowLabel} · ${formatCount(trendLoads)} loads across ${formatCount(
          trendRows.length,
        )} hourly bins · an hour with no form loads is a gap, not a zero`}
        since={experience.collected_at}
        staleAfterMs={staleAfterMs}
        tableTwin={{
          id: "fo-p95-trend",
          ariaLabel: "Hourly form load latency",
          data: trendRows,
          getRowId: (row) => row.bucket,
          columns: [
            { id: "bucket", header: "Hour", accessor: (row) => row.label },
            {
              id: "loads",
              header: "Loads",
              align: "end",
              accessor: (row) => row.loads,
              cell: (row) => <span className="tabular-nums">{formatCount(row.loads)}</span>,
            },
            {
              id: "avg",
              header: "Mean",
              align: "end",
              accessor: (row) => row.avg_ms,
              cell: (row) => <span className="tabular-nums">{formatMs(row.avg_ms)}</span>,
            },
            {
              id: "p95",
              header: "P95",
              align: "end",
              accessor: (row) => row.p95_ms,
              cell: (row) => <span className="tabular-nums">{formatMs(row.p95_ms)}</span>,
            },
          ],
        }}
      >
        {trendRows.length === 0 ? (
          <EmptyState
            size="sm"
            title="No form loads in this window"
            description="The workspace answered and held no form-load telemetry. Nothing was slow, because nothing was measured."
          />
        ) : (
          <Chart.Container
            config={{ p95_ms: { label: "P95 load duration", color: "var(--norican-chart-1)" } }}
            className="h-72"
            aria-label={`95th-percentile form load duration per hour, ${windowLabel}`}
          >
            <Chart.LineChart data={trendRows} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
              <Chart.CartesianGrid {...Chart.gridProps} vertical={false} />
              <Chart.XAxis {...Chart.axisProps} dataKey="label" />
              <Chart.YAxis
                {...Chart.axisProps}
                width={48}
                tickFormatter={(value: number) => formatMsCompact(value)}
              />
              <Chart.Tooltip
                content={
                  // `trendRows` deliberately keeps `p95_ms: null` for a quiet
                  // hour; `Number(null)` would turn that gap into "0 ms".
                  <Chart.TooltipContent formatter={(value) => formatMs(tooltipValue(value))} />
                }
              />
              <Chart.ReferenceLine
                y={experience.p95_warning_ms}
                stroke="var(--norican-warning)"
                strokeDasharray="4 4"
                label={{
                  value: `warns above ${formatMs(experience.p95_warning_ms)}`,
                  position: "insideTopRight",
                  fontSize: 11,
                  fill: "var(--norican-fg-muted)",
                }}
              />
              {/* One series, so no legend — the card title names it (§6). */}
              <Chart.Line
                {...Chart.seriesDefaults}
                dataKey="p95_ms"
                type="monotone"
                stroke="var(--color-p95_ms)"
                strokeWidth={2}
                dot={false}
              />
            </Chart.LineChart>
          </Chart.Container>
        )}
      </ChartCard>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ChartCard<FoDurationBand>
          title="Form load duration distribution"
          caption={
            bands.length === 0
              ? "Not measured"
              : `${formatCount(bandTotal)} loads, ${windowLabel} · bands at and above ${formatMs(
                  experience.p95_warning_ms,
                )} are over threshold`
          }
          since={experience.collected_at}
          staleAfterMs={staleAfterMs}
          tableTwin={{
            id: "fo-duration-bands",
            ariaLabel: "Form load duration histogram",
            data: bands,
            getRowId: (row) => row.label,
            columns: [
              { id: "band", header: "Band", accessor: (row) => row.label },
              {
                id: "loads",
                header: "Loads",
                align: "end",
                accessor: (row) => row.loads,
                cell: (row) => <span className="tabular-nums">{formatCount(row.loads)}</span>,
              },
              {
                id: "share",
                header: "Share",
                align: "end",
                accessor: (row) => row.share_pct,
                cell: (row) => <span className="tabular-nums">{formatPercent(row.share_pct)}</span>,
              },
            ],
          }}
        >
          {bands.length === 0 ? (
            <EmptyState
              size="sm"
              title="No distribution to draw"
              description="The histogram query returned no rows, so no band is shown. Every band would otherwise be present, including the genuinely empty ones."
            />
          ) : (
            <div className="flex flex-col gap-3">
              <Chart.Container
                config={{ loads: { label: "Form loads", color: "var(--norican-chart-1)" } }}
                className="h-64"
                aria-label={`Distribution of form load durations across ${bands.length} duration bands, ${windowLabel}`}
              >
                <Chart.BarChart data={bands} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
                  <Chart.CartesianGrid {...Chart.gridProps} vertical={false} />
                  <Chart.XAxis {...Chart.axisProps} dataKey="label" interval={0} />
                  <Chart.YAxis {...Chart.axisProps} width={48} allowDecimals={false} />
                  <Chart.Tooltip
                    cursor={{ fill: "var(--norican-surface-hover)" }}
                    content={
                      <Chart.TooltipContent
                        rows={(datum: FoDurationBand) => [
                          { label: "Loads", value: formatCount(datum.loads) },
                          { label: "Share of window", value: formatPercent(datum.share_pct) },
                        ]}
                      />
                    }
                  />
                  <Chart.Bar {...Chart.seriesDefaults} dataKey="loads" radius={[3, 3, 0, 0]}>
                    {bands.map((band) => (
                      <Chart.Cell
                        key={band.label}
                        fill={
                          band.lower_ms >= experience.p95_warning_ms
                            ? "var(--norican-warning)"
                            : "var(--norican-chart-1)"
                        }
                      />
                    ))}
                  </Chart.Bar>
                </Chart.BarChart>
              </Chart.Container>
              {/* The threshold split is a status, so it is never colour-alone. */}
              <div className="text-fg-muted flex flex-wrap justify-center gap-3 text-xs">
                <span className="inline-flex items-center gap-1.5">
                  <Icon icon={CircleCheck} size="xs" />
                  Within {formatMs(experience.p95_warning_ms)}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <Icon icon={TriangleAlert} size="xs" />
                  Over threshold
                </span>
              </div>
            </div>
          )}
        </ChartCard>

        <ChartCard<FoHourlyLoad>
          title="Form loads by hour of day (UTC)"
          caption={
            hourRows.length === 0
              ? "Not measured"
              : `${hourRows.length} of ${HOURS_IN_DAY} hours produced loads · hours with no telemetry are absent, not zero`
          }
          since={experience.collected_at}
          staleAfterMs={staleAfterMs}
          tableTwin={{
            id: "fo-load-by-hour",
            ariaLabel: "Form load volume by hour of day, UTC",
            data: experience.load_by_hour,
            getRowId: (row) => `${row.hour_of_day}`,
            columns: [
              {
                id: "hour",
                header: "Hour (UTC)",
                accessor: (row) => row.hour_of_day,
                cell: (row) => (
                  <span className="tabular-nums">{formatHourUtc(row.hour_of_day)}</span>
                ),
              },
              {
                id: "loads",
                header: "Loads",
                align: "end",
                accessor: (row) => row.loads,
                cell: (row) => <span className="tabular-nums">{formatCount(row.loads)}</span>,
              },
              {
                id: "avg",
                header: "Mean",
                align: "end",
                accessor: (row) => row.avg_ms,
                cell: (row) => <span className="tabular-nums">{formatMs(row.avg_ms)}</span>,
              },
              {
                id: "p95",
                header: "P95",
                align: "end",
                accessor: (row) => row.p95_ms,
                cell: (row) => <span className="tabular-nums">{formatMs(row.p95_ms)}</span>,
              },
            ],
          }}
        >
          {hourRows.length === 0 ? (
            <EmptyState
              size="sm"
              title="No hourly load profile"
              description="No hour of the day reported form loads in this window."
            />
          ) : (
            <Chart.Container
              config={{ loads: { label: "Form loads", color: "var(--norican-chart-2)" } }}
              className="h-64"
              aria-label={`Form load volume by hour of day in UTC, ${windowLabel}`}
            >
              <Chart.BarChart data={hourRows} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
                <Chart.CartesianGrid {...Chart.gridProps} vertical={false} />
                <Chart.XAxis {...Chart.axisProps} dataKey="label" interval={1} />
                <Chart.YAxis {...Chart.axisProps} width={48} allowDecimals={false} />
                <Chart.Tooltip
                  cursor={{ fill: "var(--norican-surface-hover)" }}
                  content={
                    <Chart.TooltipContent
                      rows={(datum: FoHourlyLoad) => [
                        { label: "Loads", value: formatCount(datum.loads) },
                        { label: "Mean", value: formatMs(datum.avg_ms) },
                        { label: "P95", value: formatMs(datum.p95_ms) },
                      ]}
                    />
                  }
                />
                <Chart.Bar
                  {...Chart.seriesDefaults}
                  dataKey="loads"
                  fill="var(--color-loads)"
                  radius={[3, 3, 0, 0]}
                />
              </Chart.BarChart>
            </Chart.Container>
          )}
        </ChartCard>
      </div>

      <PanelCard
        title="Slowest forms"
        caption={
          // The link renders whatever the list length, because it is the only
          // path on the board to /dynamics/forms (§4.1 — no dead ends). At
          // small N "View all 5" would be noise beside a grid already showing
          // five, so the wording changes rather than the link disappearing:
          // the L2 page still adds what this grid cannot show (P99, and the
          // whole ranking the backend produced).
          //
          // The count is `labelledForms.length` and not `forms_total` on
          // purpose: it promises what the destination holds, which is the
          // backend's fifteen slowest. How many forms the window held in all is
          // the caption under the grid, where it cannot be mistaken for the
          // size of the list behind the link.
          labelledForms.length > WORST_LIMIT ? (
            <DrillLink
              to={DYNAMICS_FORMS_PATH}
              className="text-accent text-sm font-medium hover:underline"
            >
              View all {formatCount(labelledForms.length)} ranked forms →
            </DrillLink>
          ) : (
            <span className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <span>{`${formatCount(labelledForms.length)} labelled forms ranked`}</span>
              <DrillLink
                to={DYNAMICS_FORMS_PATH}
                className="text-accent text-sm font-medium hover:underline"
              >
                Open the full ranking →
              </DrillLink>
            </span>
          )
        }
      >
        <div className="flex flex-col gap-3">
          {worstForms.length === 0 ? (
            <EmptyState
              size="sm"
              title="No form was slow enough to rank"
              description="No form-load telemetry reached the ranking query in this window."
            />
          ) : (
            <DataGrid<FoFormPerformance>
              data={worstForms}
              getRowId={(row) => row.form}
              density="compact"
              aria-label={`The ${WORST_LIMIT} slowest F&O forms by P95 load duration`}
              columns={[
                {
                  id: "form",
                  header: "Form",
                  accessor: (row) => row.form,
                  cell: (row) =>
                    row.is_other ? (
                      <span className="text-fg-muted inline-flex items-center gap-2 text-sm italic">
                        Other
                        <Badge tone="neutral" variant="outline" size="xs">
                          folded cohort
                        </Badge>
                      </span>
                    ) : (
                      <span className="text-fg text-sm font-medium">{row.form}</span>
                    ),
                },
                {
                  id: "loads",
                  header: "Loads",
                  align: "end",
                  accessor: (row) => row.loads,
                  cell: (row) => <span className="tabular-nums">{formatCount(row.loads)}</span>,
                },
                {
                  id: "users",
                  header: "Users",
                  align: "end",
                  accessor: (row) => row.distinct_users,
                  cell: (row) => (
                    <span className="tabular-nums">{formatCount(row.distinct_users)}</span>
                  ),
                },
                {
                  id: "p95",
                  header: "P95",
                  align: "end",
                  accessor: (row) => row.p95_ms,
                  cell: (row) => <span className="tabular-nums">{formatMs(row.p95_ms)}</span>,
                },
                {
                  id: "verdict",
                  header: "Against threshold",
                  accessor: (row) => (row.over_threshold ? "over" : "within"),
                  cell: (row) =>
                    row.p95_ms == null ? (
                      <span className="text-fg-muted text-xs">no percentile</span>
                    ) : (
                      <Badge
                        tone={row.over_threshold ? "warning" : "success"}
                        variant="subtle"
                        size="xs"
                      >
                        {row.over_threshold
                          ? `over ${formatMs(experience.p95_warning_ms)}`
                          : "within threshold"}
                      </Badge>
                    ),
                },
              ]}
            />
          )}
          <Typography variant="body-sm" tone="muted">
            {rankingScopeCaption(labelledForms.length, experience.forms_total)}
          </Typography>
          <Typography variant="body-sm" tone="muted">
            {suppressionCaption(experience.suppression, "loads")}
          </Typography>
        </div>
      </PanelCard>
    </section>
  );
}
